import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { obtenerPrograma } from '../../servicios/programaService';
import { obtenerConfigCalidad, obtenerConfigRegistroCalidad } from '../../servicios/calidadService';
import ConfiguracionCalidad from './ConfiguracionCalidad';
import '../estilos/Calidad.css';

const CARGOS_CONTROL = ['Jefe de terreno', 'Calidad', 'Administrador de obra'];

export default function DashboardCalidad() {
  const [datos, setDatos] = useState({ programa: null, secciones: [], actividades: [] });
  const [actividadesCalidad, setActividadesCalidad] = useState([]);
  const [actividadesRegistro, setActividadesRegistro] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [mostrarConfig, setMostrarConfig] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [prog, config, configRegistro] = await Promise.all([
        obtenerPrograma(), obtenerConfigCalidad(), obtenerConfigRegistroCalidad()
      ]);
      setDatos(prog);
      setActividadesCalidad(config);
      setActividadesRegistro(configRegistro);
    } catch {
      // silencioso
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Actividades seleccionadas como entrega departamento
  const actividadesSet = useMemo(() => new Set(actividadesCalidad), [actividadesCalidad]);

  const actividadesSeleccionadas = useMemo(
    () => datos.actividades.filter(a => a.wbsCode && actividadesSet.has(a.wbsCode)),
    [datos.actividades, actividadesSet]
  );

  // KPIs por etapa del control ITO (R0 → R1 → RF)
  const kpisEtapas = useMemo(() => {
    let r0 = 0, r1 = 0, rf = 0, entregado = 0;
    for (const act of actividadesSeleccionadas) {
      if ((act.porcentajeAvance ?? 0) < 100) {
        r0++;
        continue;
      }
      const estado = act.estadoITO || 'pendiente';
      if (estado === 'rf') rf++;
      else if (estado === 'entregado') entregado++;
      else r1++;
    }
    return { r0, r1, rf, entregado };
  }, [actividadesSeleccionadas]);

  // Registros: pendientes/completados por cargo asignado (una misma partida puede
  // sumar a más de un cargo, ya que la asignación se sortea con solape) + cuántos
  // protocolos observados quedan pendientes de revisión de Supervisor.
  const statsRegistro = useMemo(() => {
    const mapaConfig = {};
    for (const item of actividadesRegistro) {
      mapaConfig[item.wbsCode] = item;
    }
    const porCargo = {};
    for (const cargo of CARGOS_CONTROL) porCargo[cargo] = { total: 0, completados: 0, pendientes: 0 };
    let observadosSupervisor = 0;

    for (const act of datos.actividades) {
      const config = act.wbsCode ? mapaConfig[act.wbsCode] : undefined;
      if (!config || !config.protocolos?.length) continue;
      for (const protocolo of config.protocolos) {
        const item = (act.registroCalidadProtocolos || []).find(p => p.protocolo === protocolo);
        const estado = item?.estado || 'pendiente';
        if (estado === 'observado') observadosSupervisor++;
        for (const cargo of (config.cargosAsignados || [])) {
          if (!porCargo[cargo]) continue;
          porCargo[cargo].total++;
          if (estado === 'aprobado') porCargo[cargo].completados++;
          else porCargo[cargo].pendientes++;
        }
      }
    }
    return { porCargo, observadosSupervisor };
  }, [datos.actividades, actividadesRegistro]);

  if (cargando) {
    return <div className="calidad-loading">Cargando Calidad…</div>;
  }

  if (!datos.programa) {
    return (
      <div className="calidad-container">
        <div className="calidad-empty">
          <div className="calidad-empty-icon">📋</div>
          <h3>No hay programa de obra importado</h3>
          <p>Para usar el módulo Calidad, primero importa un programa de obra en el módulo Programa de Obra.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="calidad-container">
      {mostrarConfig && (
        <ConfiguracionCalidad
          actividades={datos.actividades}
          secciones={datos.secciones}
          actividadesActuales={actividadesCalidad}
          onGuardado={(nuevas) => { setActividadesCalidad(nuevas); setMostrarConfig(false); }}
          onCerrar={() => setMostrarConfig(false)}
        />
      )}

      <div className="calidad-header">
        <h2>Calidad</h2>
        <div className="calidad-header-actions">
          <button className="calidad-btn-secondary" onClick={() => setMostrarConfig(true)}>
            ⚙ Configurar partidas
          </button>
        </div>
      </div>

      {actividadesCalidad.length === 0 ? (
        <div className="calidad-empty">
          <div className="calidad-empty-icon">🔍</div>
          <h3>Sin partidas de Calidad configuradas</h3>
          <p>
            Selecciona qué partidas del programa corresponden a Calidad
            (observaciones, punch list, no conformidades, etc.).
          </p>
          <button className="calidad-btn-primary" onClick={() => setMostrarConfig(true)}>
            Seleccionar partidas de Calidad
          </button>
        </div>
      ) : (
        <>
          {/* KPIs por etapa del control ITO */}
          <div className="calidad-kpis">
            <div className="calidad-kpi-card">
              <div className="kpi-label">En R0</div>
              <div className={`kpi-value ${kpisEtapas.r0 > 0 ? '' : 'verde'}`}>{kpisEtapas.r0}</div>
            </div>
            <div className="calidad-kpi-card">
              <div className="kpi-label">En R1</div>
              <div className={`kpi-value ${kpisEtapas.r1 > 0 ? '' : 'verde'}`}>{kpisEtapas.r1}</div>
            </div>
            <div className="calidad-kpi-card">
              <div className="kpi-label">En RF</div>
              <div className={`kpi-value ${kpisEtapas.rf > 0 ? '' : 'verde'}`}>{kpisEtapas.rf}</div>
            </div>
            <div className="calidad-kpi-card">
              <div className="kpi-label">Deptos entregados</div>
              <div className="kpi-value verde">{kpisEtapas.entregado}</div>
            </div>
          </div>
        </>
      )}

      {/* Registros: pendientes/completados por cargo (independiente de Entrega Departamento) */}
      {actividadesRegistro.length > 0 && (
        <>
          <div className="calidad-header" style={{ marginTop: 8 }}>
            <h2>Registros por cargo</h2>
          </div>
          <div className="calidad-secciones-grid">
            {CARGOS_CONTROL.map(cargo => {
              const s = statsRegistro.porCargo[cargo];
              const pct = s.total > 0 ? Math.round((s.completados / s.total) * 100) : 0;
              return (
                <div key={cargo} className="calidad-seccion-card">
                  <h4 title={cargo}>{cargo}</h4>
                  <div className="calidad-seccion-pct">{pct}%</div>
                  <div className="calidad-seccion-progress-bar">
                    <div className="calidad-seccion-progress-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="calidad-seccion-chips">
                    <span className="calidad-chip calidad-chip-total">{s.total} asignados</span>
                    {s.completados > 0 && (
                      <span className="calidad-chip calidad-chip-ok">{s.completados} completados</span>
                    )}
                    {s.pendientes > 0 && (
                      <span className="calidad-chip calidad-chip-atrasada">{s.pendientes} pendientes</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {statsRegistro.observadosSupervisor > 0 && (
            <div className="calidad-kpis">
              <div className="calidad-kpi-card">
                <div className="kpi-label">Pendientes de revisión (Supervisor)</div>
                <div className="kpi-value">{statsRegistro.observadosSupervisor}</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
